import type { ScanCaptureDetails, ScanQuickWarpDiagnostics } from "../types/scanWorkflow";
import { productionExecutionProfileEvidence, recognitionParallelismEvidence } from "./recognitionParallelismProbeService";
import { fullFidelityPerformanceEvidence } from "./recognitionPerformanceContract";
import { getParallelRuntimeSoakSnapshot } from "./parallelRuntimeSoakService";

async function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = "async";
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function wrapLine(context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (current && context.measureText(next).width > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function formatCorners(corners: { x: number; y: number }[] | null | undefined): string {
  if (!corners?.length) return "–";
  return corners.map((point, index) => `P${index + 1} ${point.x.toFixed(4)}/${point.y.toFixed(4)}`).join(" · ");
}

function formatGeometry(prefix: string, geometry: { checked: boolean; accepted: boolean; areaRatio: number | null; aspectRatio: number | null; geometryScore: number | null; convex: boolean | null; plausibleBounds: boolean | null; reason: string | null }): string {
  return `${prefix} · ${geometry.accepted ? "akzeptiert" : "abgelehnt"} · Fläche ${geometry.areaRatio === null ? "–" : `${(geometry.areaRatio * 100).toFixed(2)} %`} · Aspect ${geometry.aspectRatio === null ? "–" : geometry.aspectRatio.toFixed(3)} · Geo ${geometry.geometryScore === null ? "–" : geometry.geometryScore.toFixed(3)} · konvex ${geometry.convex === null ? "–" : geometry.convex ? "ja" : "nein"} · Bounds ${geometry.plausibleBounds === null ? "–" : geometry.plausibleBounds ? "ja" : "nein"} · ${geometry.reason ?? "–"}`;
}

function buildLines(details: ScanCaptureDetails): string[] {
  const lines: string[] = [];
  lines.push(`Identität · #ID ${details.identity?.withdrawalNumber ?? "–"} · SID ${details.identity?.serverId ?? "noch nicht synchronisiert"} · UUID ${details.identity?.clientUuid ?? "–"}`);
  lines.push(`Aufnahme · ${details.captureSource === "image-capture" ? "ImageCapture.takePhoto()" : "Videoframe-Fallback"}`);
  const executionProfile = productionExecutionProfileEvidence(details);
  const parallelismProbe = recognitionParallelismEvidence();
  lines.push(`Runtime Execution · ${String(executionProfile.profile_id ?? "serial-production-v1")} · Parallel Recognition ${executionProfile.production_parallel_recognition_enabled ? "AN" : "AUS"} · Detection Worker ${executionProfile.detection_worker_budget ?? 1} · OCR Worker ${executionProfile.ocr_worker_budget ?? 1}`);
  lines.push(`Parallelitäts-Probe · ${String(parallelismProbe.status ?? "not_run")} · CPU ${parallelismProbe.logical_cpu_count ?? "–"} · Worker ${parallelismProbe.created_workers ?? 0}/${parallelismProbe.requested_workers ?? 2} · ${String(parallelismProbe.classification ?? "pending")} · Speedup ${typeof parallelismProbe.estimated_speedup === "number" ? `${parallelismProbe.estimated_speedup.toFixed(2)}×` : "–"} · Seq ${parallelismProbe.sequential_wall_ms ?? "–"} ms · Parallel ${parallelismProbe.parallel_wall_ms ?? "–"} ms`);
  lines.push(`Browser Parallel-Capabilities · Worker ${parallelismProbe.worker_api_available ? "ja" : "nein"} · OffscreenCanvas ${parallelismProbe.offscreen_canvas_available ? "ja" : "nein"} · ImageBitmap ${parallelismProbe.create_image_bitmap_available ? "ja" : "nein"} · COI ${parallelismProbe.cross_origin_isolated ? "ja" : "nein"} · SAB ${parallelismProbe.shared_array_buffer_available ? "ja" : "nein"} · Atomics ${parallelismProbe.atomics_available ? "ja" : "nein"} · WASM-Pthreads-Voraussetzung ${parallelismProbe.wasm_pthreads_prerequisites_met ? "ja" : "nein"}`);
  const performance = fullFidelityPerformanceEvidence(details);
  if (performance.duration_ms != null) {
    if (performance.eligible) {
      const actual = performance.duration_ms;
      const target = performance.target_ms;
      lines.push(`PV-5 Full-Fidelity Performance · Shutter→Step 2 ${actual.toFixed(0)} ms · Ziel ${target} ms · Delta ${actual <= target ? "−" : "+"}${Math.abs(actual - target).toFixed(0)} ms · ${performance.verdict === "pass" ? "TARGET PASS" : "TARGET OFFEN"} · Qualität vollständig`);
    } else {
      lines.push(`PV-5 Full-Fidelity Performance · Shutter→Step 2 ${performance.duration_ms.toFixed(0)} ms · NICHT WERTBAR · ${performance.reason_codes.join(", ") || "FULL_FIDELITY_REQUIRED"} · keine Qualitätsreduktion`);
    }
  }
  const soak = details.parallelRuntimeSoak ?? getParallelRuntimeSoakSnapshot();
  lines.push(`PV-5/PV-10 QA-Soak · ${soak.gates.state} · Full-Fidelity ${soak.fullFidelitySamples}/${soak.totalSamples} · OpenCV warm PASS ${soak.opencv.warmReusedFullFidelityPasses}/${soak.requiredWarmFullFidelitySamples} · OCR warm PASS ${soak.ocr.warmReusedFullFidelityPasses}/${soak.requiredWarmFullFidelitySamples} · PV-6 Batch warm PASS ${soak.criticalPathCandidateBatch.warmReusedFullFidelityPasses}/${soak.requiredWarmFullFidelitySamples} · Parity ${soak.gates.cleanParityWindow ? "sauber" : "auffällig"} · Historische QA ${soak.gates.productionActivationAllowed ? "qualifiziert" : "noch offen"}`);
  lines.push(`PV-11 Runtime-First · Benutzer-Onboarding-Soak NEIN · Freigabe pro Session = Worker/CPU/Memory Admission + echte 2-Worker-Probe + Checksum + warme Pools · Serial-Fallback immer aktiv`);
  lines.push(`PV-10 Release-Qualifikation · Quelle ${soak.qualification.source} · Release ${soak.qualification.releaseStatus}${soak.qualification.releaseProfileId ? `/${soak.qualification.releaseProfileId}` : ""} · lokaler Dev-Soak ${soak.qualification.localSoakReady ? "PASS" : "offen"} · nur QA/Release-Evidence · ${soak.qualification.reason}`);
  lines.push(`PV-7 Acceptance · Worker-Recovery ${soak.gates.workerFailureRecoveryEvidence ? "PASS" : "offen"} (${soak.acceptance.workerFailureRecovery.passes}/${soak.acceptance.workerFailureRecovery.attempts}) · Memory-Pressure→Serial ${soak.gates.memoryPressureFallbackEvidence ? "PASS" : "offen"} (${soak.acceptance.memoryPressureFallback.passes}/${soak.acceptance.memoryPressureFallback.attempts}) · Background ${soak.gates.backgroundRecoveryEvidence ? "PASS" : "offen"} · QA-Evidence ${soak.gates.productionActivationAllowed ? "vollständig" : "sammelt weiter"}`);
  const pv15Critical = details.criticalPathOrchestration ?? null;
  if (pv15Critical?.contractVersion === "pv15-full-fidelity-performance-freeze-v1") {
    lines.push(`PV-15 Performance Freeze · Detection-Evidence ${pv15Critical.detectionEvidenceElapsedMs ?? "–"} ms / Wait ${pv15Critical.detectionEvidenceCriticalPathWaitMs ?? "–"} ms · ${pv15Critical.detectionEvidenceMode ?? "legacy-mode"} · RectInput ${pv15Critical.rectificationInputPrepareElapsedMs ?? "–"} ms / Wait ${pv15Critical.rectificationInputCriticalPathWaitMs ?? "–"} ms · Final-OCR ${pv15Critical.speculativeFinalOcrStatus ?? "not_started"} ${pv15Critical.speculativeFinalOcrElapsedMs ?? "–"} ms / Wait ${pv15Critical.speculativeFinalOcrCriticalPathWaitMs ?? "–"} ms`);
  }
  const pv14Detection = details.detection?.parallelDetectionCandidateEvaluation ?? null;
  if (pv14Detection) {
    lines.push(`PV-14 Detection Candidate-Eval · ${pv14Detection.status.toUpperCase()} · Worker ${pv14Detection.effectiveWorkers}/${pv14Detection.requestedWorkers} · Pool ${pv14Detection.poolWarm ? "warm" : "nicht warm"} · Kandidaten ${pv14Detection.candidateCount} · Fallback ${pv14Detection.serialFallbackUsed ? "JA" : "nein"}`);
    if (pv14Detection.status === "parallel") {
      lines.push(`PV-14 Timing · Parallel ${pv14Detection.parallelWallMs ?? "–"} ms · Seq≈ ${pv14Detection.serialEquivalentMs ?? "–"} ms · Speedup ${pv14Detection.estimatedSpeedup === null ? "–" : `${pv14Detection.estimatedSpeedup.toFixed(2)}×`} · Overlap ${pv14Detection.overlapMs ?? "–"} ms / ${pv14Detection.overlapRatio === null ? "–" : `${(pv14Detection.overlapRatio * 100).toFixed(1)} %`} · Tasks lokal/remote ${pv14Detection.localTaskCount}/${pv14Detection.remoteTaskCount} · ${pv14Detection.transferMode}`);
    } else if (pv14Detection.reason) {
      lines.push(`PV-14 Serial-Fallback · ${pv14Detection.reason}${pv14Detection.error ? ` · ${pv14Detection.error}` : ""}`);
    }
  }
  const pv8Production = details.rectification?.productionParallelCandidateBatch ?? null;
  if (pv8Production) {
    lines.push(`PV-8 Production Candidate-Batch · ${pv8Production.status.toUpperCase()} · ${pv8Production.profileId} · Gate ${pv8Production.activationGateSatisfied ? "READY" : "zu"} · Admission ${pv8Production.admissionAllowed ? "PASS" : "nein"} · Probe ${pv8Production.probeReady ? "PASS" : "nein"} · Pools ${pv8Production.poolsWarm ? "warm" : "nicht warm"} · Fallback ${pv8Production.serialFallbackUsed ? "JA" : "nein"}`);
    if (pv8Production.status === "parallel") {
      lines.push(`PV-8 Critical Path · Kandidaten ${pv8Production.candidateCount} · Gewinner #${pv8Production.selectedCandidateIndex ?? "–"} · Wall ${pv8Production.parallelWallMs ?? "–"} ms · Rectify ${pv8Production.rectifyWallMs ?? "–"} ms · Rerank-OCR ${pv8Production.rerankOcrWallMs ?? "–"} ms · Overlap ${pv8Production.overlapMs ?? "–"} ms`);
    } else if (pv8Production.reason) {
      lines.push(`PV-8 Serial-Fallback · ${pv8Production.reason}${pv8Production.error ? ` · ${pv8Production.error}` : ""}`);
    }
  }
  const pv9Ocr = details.ocr?.parallelProduction ?? null;
  if (pv9Ocr) {
    lines.push(`PV-9 Production Final-OCR · ${pv9Ocr.status.toUpperCase()} · ${pv9Ocr.profileId} · Gate ${pv9Ocr.activationGateSatisfied ? "READY" : "zu"} · Admission ${pv9Ocr.admissionAllowed ? "PASS" : "nein"} · Probe ${pv9Ocr.probeReady ? "PASS" : "nein"} · Pools ${pv9Ocr.poolsWarm ? "warm" : "nicht warm"} · Fallback ${pv9Ocr.serialFallbackUsed ? "JA" : "nein"}`);
    if (pv9Ocr.status === "parallel") {
      lines.push(`PV-9 Final-OCR Critical Path · Worker ${pv9Ocr.effectiveWorkers}/${pv9Ocr.requestedWorkers} effektiv · Peak ${pv9Ocr.activeWorkersPeak} · Wall ${pv9Ocr.parallelWallMs ?? "–"} ms · Overlap ${pv9Ocr.overlapMs ?? "–"} ms / ${pv9Ocr.overlapRatio === null ? "–" : `${(pv9Ocr.overlapRatio * 100).toFixed(1)} %`} · Startup ${pv9Ocr.workerStartupMs ?? "–"} ms · Merge ${pv9Ocr.mergeOrder.join("→")}`);
      for (const task of pv9Ocr.tasks) lines.push(`PV-9 Task · ${task.workerId} · ${task.field} · ${task.executionContext} · ${task.transferMode} · ${task.startedOffsetMs.toFixed(1)}→${task.finishedOffsetMs.toFixed(1)} ms · ${task.durationMs} ms · ${task.executionId}`);
    } else if (pv9Ocr.reason) {
      lines.push(`PV-9 Serial-Fallback · ${pv9Ocr.reason}${pv9Ocr.error ? ` · ${pv9Ocr.error}` : ""}`);
    }
  }
  const pv12Rescue = details.rectification?.productionParallelNonPlanarRescue ?? null;
  if (pv12Rescue) {
    lines.push(`PV-12 Production Non-Planar Rescue · ${pv12Rescue.status.toUpperCase()} · ${pv12Rescue.profileId} · Hard Case ${pv12Rescue.hardCase ?? "–"} · Worker ${pv12Rescue.effectiveWorkers}/${pv12Rescue.requestedWorkers} · Fallback ${pv12Rescue.serialFallbackUsed ? "JA" : "nein"}`);
    if (pv12Rescue.status === "parallel") {
      lines.push(`PV-12 Critical Path · Dewarp ${pv12Rescue.parallelDewarpWallMs ?? "–"} ms · OCR-Probes ${pv12Rescue.parallelOcrWallMs ?? "–"} ms · Overlap ${pv12Rescue.overlapMs ?? "–"} ms / ${pv12Rescue.overlapRatio === null ? "–" : `${(pv12Rescue.overlapRatio * 100).toFixed(1)} %`} · Candidate ${pv12Rescue.candidateGenerated ? "ja" : "nein"} · Apply ${pv12Rescue.candidateApplied ? "ja" : "nein"} · ${pv12Rescue.transferMode ?? "–"}`);
    } else if (pv12Rescue.reason) {
      lines.push(`PV-12 Serial-Fallback · ${pv12Rescue.reason}${pv12Rescue.error ? ` · ${pv12Rescue.error}` : ""}`);
    }
  }
  const rectificationTiming = details.rectification?.timing ?? null;
  if (rectificationTiming) {
    lines.push(`Rectification Timing · Total ${rectificationTiming.totalMs === null ? "–" : `${rectificationTiming.totalMs.toFixed(0)} ms`} · Prepare ${rectificationTiming.inputPrepareMs === null ? "–" : `${rectificationTiming.inputPrepareMs.toFixed(0)} ms`} · Input-SHA ${rectificationTiming.inputShaMs === null ? "–" : `${rectificationTiming.inputShaMs.toFixed(0)} ms`} · Worker-RT ${rectificationTiming.workerRoundTripMs === null ? "–" : `${rectificationTiming.workerRoundTripMs.toFixed(0)} ms`} · Encode ${rectificationTiming.outputEncodeMs === null ? "–" : `${rectificationTiming.outputEncodeMs.toFixed(0)} ms`}`);
    lines.push(`Rectification Detail · Kandidaten ${rectificationTiming.candidateProbes} · Rerank-OCR ${rectificationTiming.rerankOcrMs === null ? "–" : `${rectificationTiming.rerankOcrMs.toFixed(0)} ms`} · Template ${rectificationTiming.templateEvaluationMs === null ? "–" : `${rectificationTiming.templateEvaluationMs.toFixed(0)} ms`} · NonPlanar ${rectificationTiming.nonPlanarRescueMs === null ? "–" : `${rectificationTiming.nonPlanarRescueMs.toFixed(0)} ms`} · Output-SHA ${rectificationTiming.outputShaMs === null ? "–" : `${rectificationTiming.outputShaMs.toFixed(0)} ms`} · Input-Archiv ${rectificationTiming.inputArchiveElapsedMs === null ? "deferred" : `${rectificationTiming.inputArchiveElapsedMs.toFixed(0)} ms`} · Mode ${rectificationTiming.inputArchiveMode} · CP-Wait ${rectificationTiming.inputArchiveCriticalPathWaitMs ?? "–"} ms`);
  }
  const openCvShadow = details.rectification?.parallelShadow ?? null;
  if (openCvShadow) {
    lines.push(`PV-3 OpenCV Shadow · ${openCvShadow.status.toUpperCase()} · ${openCvShadow.profileId} · Production beeinflusst ${openCvShadow.productionInfluenced ? "JA" : "nein"} · Worker ${openCvShadow.effectiveWorkers}/${openCvShadow.requestedWorkers} effektiv · Peak ${openCvShadow.activeWorkersPeak} · Kandidaten ${openCvShadow.candidateCount}`);
    lines.push(`PV-3 Timing · Serial ${openCvShadow.serialWallMs ?? "–"} ms · Parallel ${openCvShadow.parallelWallMs ?? "–"} ms · Speedup ${openCvShadow.estimatedSpeedup === null ? "–" : `${openCvShadow.estimatedSpeedup.toFixed(2)}×`} · Overlap ${openCvShadow.overlapMs ?? "–"} ms / ${openCvShadow.overlapRatio === null ? "–" : `${(openCvShadow.overlapRatio * 100).toFixed(1)} %`} · Startup ${openCvShadow.workerStartupMs ?? "–"} ms · Decode ${openCvShadow.inputDecodeMs ?? "–"} ms · Pool ${openCvShadow.workerPoolWarm ? "warm" : "cold"}/${openCvShadow.workerPoolReused ? "reused" : "fresh"} #${openCvShadow.workerPoolReuseCount} · Retain ${openCvShadow.workersRetainedForReuse ? "ja" : "nein"}`);
    lines.push(`PV-3 Transport · ${openCvShadow.executionContext} · ${openCvShadow.transferMode} · Input-RGBA ${openCvShadow.sourceInputRgbaShaMatch === null ? "nicht vergleichbar" : openCvShadow.sourceInputRgbaShaMatch ? "PASS" : "FAIL"} · Production-Output ${openCvShadow.parity.productionSelectedOutputMatch === null ? "–" : openCvShadow.parity.productionSelectedOutputMatch ? "PASS" : "FAIL"}`);
    lines.push(`PV-3 Parity · Warp-SHAs ${openCvShadow.parity.allRectifiedShaMatch === null ? "–" : openCvShadow.parity.allRectifiedShaMatch ? "PASS" : "FAIL"} · Order ${openCvShadow.parity.deterministicOrderMatch === null ? "–" : openCvShadow.parity.deterministicOrderMatch ? "PASS" : "FAIL"} · Gesamt ${openCvShadow.parity.parallelParityPass === null ? "–" : openCvShadow.parity.parallelParityPass ? "PASS" : "FAIL"}`);
    const pv6Batch = openCvShadow.criticalPathCandidateBatch;
    if (pv6Batch) {
      lines.push(`PV-6 Critical-Path Batch · ${pv6Batch.status.toUpperCase()} · Full-Fidelity ${pv6Batch.fullFidelityPreserved ? "JA" : "NEIN"} · Production beeinflusst ${pv6Batch.productionInfluenced ? "JA" : "nein"} · Kandidaten ${pv6Batch.candidateCount} · Gewinner ${pv6Batch.selectedCandidateIndexSerial ?? "–"}→${pv6Batch.selectedCandidateIndexParallel ?? "–"}`);
      lines.push(`PV-6 Timing · Production-Batch ${pv6Batch.productionCandidateBatchWallMs ?? "–"} ms · Parallel-Shadow ${pv6Batch.parallelCandidateBatchWallMs ?? "–"} ms · Speedup ${pv6Batch.estimatedSpeedup === null ? "–" : `${pv6Batch.estimatedSpeedup.toFixed(2)}×`} · Encode ${pv6Batch.outputEncodeWallMs ?? "–"} ms · Rerank-OCR ${pv6Batch.rerankOcrWallMs ?? "–"} ms · Template ${pv6Batch.templateEvaluationWallMs ?? "–"} ms`);
      lines.push(`PV-6 Parity · Winner ${pv6Batch.deterministicWinnerMatch === null ? "–" : pv6Batch.deterministicWinnerMatch ? "PASS" : "FAIL"} · Gesamt ${pv6Batch.parallelParityPass === null ? "–" : pv6Batch.parallelParityPass ? "PASS" : "FAIL"} · Qualität vollständig`);
      for (const candidate of pv6Batch.candidateParity) lines.push(`PV-6 Candidate #${candidate.candidateIndex} · Warp ${candidate.rectifiedShaMatch ? "PASS" : "FAIL"} · OCR ${candidate.materialNumberMatch && candidate.confidenceMatch ? "PASS" : "FAIL"} · Template ${candidate.templateScoreMatch && candidate.templateResidualMatch ? "PASS" : "FAIL"} · Score ${candidate.rerankScoreMatch ? "PASS" : "FAIL"}`);
      if (pv6Batch.fallbackReason) lines.push(`PV-6 Fallback · ${pv6Batch.fallbackReason}${pv6Batch.error ? ` · ${pv6Batch.error}` : ""}`);
    }
    for (const task of openCvShadow.parallelTasks) lines.push(`PV-3 Task · ${task.workerId} · ${task.candidateId} · ${task.executionContext} · ${task.transferMode} · ${task.startedOffsetMs.toFixed(1)}→${task.finishedOffsetMs.toFixed(1)} ms · Warp ${task.timings.warpPerspectiveMs.toFixed(1)} ms · Mat ${task.timings.inputToMatMs.toFixed(1)} ms · Readback ${task.timings.readbackMs.toFixed(1)} ms · SHA ${task.outputSha256}`);
    if (openCvShadow.fallbackReason) lines.push(`PV-3 Fallback · ${openCvShadow.fallbackReason}${openCvShadow.error ? ` · ${openCvShadow.error}` : ""}`);
  }
  const parallelShadow = details.ocr?.parallelShadow ?? null;
  if (parallelShadow) {
    lines.push(`PV-2 Same-RAW Shadow · ${parallelShadow.status.toUpperCase()} · ${parallelShadow.profileId} · Production beeinflusst ${parallelShadow.productionInfluenced ? "JA" : "nein"} · Worker ${parallelShadow.effectiveWorkers}/${parallelShadow.requestedWorkers} effektiv · Peak ${parallelShadow.activeWorkersPeak}`);
    lines.push(`PV-2 Timing · Serial ${parallelShadow.serialWallMs ?? "–"} ms · Parallel ${parallelShadow.parallelWallMs ?? "–"} ms · Speedup ${parallelShadow.estimatedSpeedup === null ? "–" : `${parallelShadow.estimatedSpeedup.toFixed(2)}×`} · Overlap ${parallelShadow.overlapMs ?? "–"} ms / ${parallelShadow.overlapRatio === null ? "–" : `${(parallelShadow.overlapRatio * 100).toFixed(1)} %`} · Startup ${parallelShadow.workerStartupMs ?? "–"} ms · Pool ${parallelShadow.workerPoolWarm ? "warm" : "cold"}/${parallelShadow.workerPoolReused ? "reused" : "fresh"} #${parallelShadow.workerPoolReuseCount} · Retain ${parallelShadow.workersRetainedForReuse ? "ja" : "nein"}`);
    lines.push(`PV-2 Parity · Recognition ${parallelShadow.parity.recognitionParityPass === null ? "–" : parallelShadow.parity.recognitionParityPass ? "PASS" : "FAIL"} · Grid ${parallelShadow.parity.gridAnchoringMatch === null ? "–" : parallelShadow.parity.gridAnchoringMatch ? "gleich" : "abweichend"} · ROI ${parallelShadow.parity.allRoiShaMatch === null ? "–" : parallelShadow.parity.allRoiShaMatch ? "gleich" : "abweichend"} · Text ${parallelShadow.parity.allTextMatch === null ? "–" : parallelShadow.parity.allTextMatch ? "gleich" : "abweichend"} · Confidence ${parallelShadow.parity.allConfidenceMatch === null ? "–" : parallelShadow.parity.allConfidenceMatch ? "gleich" : "abweichend"} · Pass ${parallelShadow.parity.allPassSelectionMatch === null ? "–" : parallelShadow.parity.allPassSelectionMatch ? "gleich" : "abweichend"} · 4 Felder ${parallelShadow.parity.finalFourFieldsMatch === null ? "–" : parallelShadow.parity.finalFourFieldsMatch ? "gleich" : "abweichend"}`);
    lines.push(`PV-2 Input · RAW-decoded ${parallelShadow.rawDecodedSha256 ?? "–"} · Analyse ${parallelShadow.analysisInputSha256 ?? "–"} · Rectified-File ${parallelShadow.rectifiedFileSha256 ?? "–"} · Rectification-RGBA ${parallelShadow.rectificationOutputRgbaSha256 ?? "–"}`);
    for (const task of parallelShadow.tasks) lines.push(`PV-2 Task · ${task.workerId} · ${task.field} · ${task.executionContext} · ${task.transferMode} · ${task.startedOffsetMs.toFixed(1)}→${task.finishedOffsetMs.toFixed(1)} ms · ${task.durationMs} ms · ${task.executionId}`);
    for (const field of parallelShadow.fieldParity) lines.push(`PV-2 ${field.field} · Text ${field.textMatch ? "PASS" : "FAIL"} · Conf ${field.confidenceMatch ? "PASS" : "FAIL"} · Pass ${field.selectedPassMatch && field.passSequenceMatch ? "PASS" : "FAIL"} · ROI ${field.roiShaMatch ? "PASS" : "FAIL"} · StorageDecoder ${field.storageDecoderMatch ? "PASS" : "FAIL"}`);
    if (parallelShadow.fallbackReason) lines.push(`PV-2 Fallback · ${parallelShadow.fallbackReason}${parallelShadow.error ? ` · ${parallelShadow.error}` : ""}`);
  }
  if (details.quality) {
    lines.push(`Qualität · Schärfe ${details.quality.sharpness.toFixed(1)} · Helligkeit ${details.quality.brightness.toFixed(0)} · Kontrast ${details.quality.contrast.toFixed(1)}`);
  }
  if (details.mapping) {
    lines.push(`Preview→Foto · ${details.mapping.method} · Confidence ${details.mapping.confidence.toFixed(3)} · Matches ${details.mapping.matchCount} · Inlier ${details.mapping.inlierCount} (${(details.mapping.inlierRatio * 100).toFixed(1)} %)`);
    lines.push(`Mapping-Ecken · ${formatCorners(details.mapping.photoCorners)}`);
    lines.push(`Mapping-Bilder · Preview ${details.mapping.previewWidth ?? "–"}×${details.mapping.previewHeight ?? "–"} · Fotoanalyse ${details.mapping.photoAnalysisWidth ?? "–"}×${details.mapping.photoAnalysisHeight ?? "–"}`);
    if (details.mapping.searchRoi) {
      const roi = details.mapping.searchRoi;
      lines.push(`Search-ROI normiert · L ${roi.left.toFixed(4)} · T ${roi.top.toFixed(4)} · R ${roi.right.toFixed(4)} · B ${roi.bottom.toFixed(4)}`);
      lines.push(`Search-ROI Pixel · L ${roi.pixelLeft ?? "–"} · T ${roi.pixelTop ?? "–"} · R ${roi.pixelRight ?? "–"} · B ${roi.pixelBottom ?? "–"} · ${roi.pixelWidth ?? "–"}×${roi.pixelHeight ?? "–"}`);
      if (roi.mappingQuality !== undefined && roi.mappingQuality !== null) {
        lines.push(`Adaptive Search-ROI · Mapping-Qualität ${roi.mappingQuality.toFixed(3)} · H +${((roi.adaptiveHorizontalPadding ?? 0) * 100).toFixed(0)} % · V +${((roi.adaptiveVerticalPadding ?? 0) * 100).toFixed(0)} % · ${roi.adaptiveReason ?? "–"}`);
      }
    }
    lines.push(`Mapping Grid · coarse ${details.mapping.gridCoarseUsed ? "ja" : "nein"} · Preview ${details.mapping.previewGridScore?.toFixed(3) ?? "–"} · Foto ${details.mapping.photoGridScore?.toFixed(3) ?? "–"}`);
  }
  if (details.detection) {
    const detection = details.detection;
    lines.push(`Erkennung · ${detection.found ? "Etikett erkannt" : "nicht erkannt"} · Final ${detection.finalGeometryAccepted ? "akzeptiert" : "abgelehnt"} · Methode ${detection.detectionMethod} · Score ${detection.score.toFixed(3)}`);
    lines.push(`Detection-Bild · ${detection.imageWidth}×${detection.imageHeight} · Konturen ${detection.contourCount} · Kandidaten ${detection.candidateCount} · Hough ${detection.houghLineCount}`);
    lines.push(`Detection-Geometrie · Fläche ${detection.areaRatio === null ? "–" : `${(detection.areaRatio * 100).toFixed(2)} %`} · Rechteckigkeit ${detection.rectangularity === null ? "–" : detection.rectangularity.toFixed(3)}`);
    lines.push(`Grid · Score ${detection.gridScore.toFixed(3)} · Linien H ${detection.gridMatchedHorizontal}/${detection.gridHorizontalLines} · V ${detection.gridMatchedVertical}/${detection.gridVerticalLines} · Homographie-Inlier ${detection.homographyInliers}`);
    lines.push(`Grid-Linienpositionen H · ${(detection.gridHorizontalPositions ?? []).map((value, index) => `H${index + 1}=${value.toFixed(4)}`).join(" · ") || "–"}`);
    lines.push(`Grid-Linienpositionen V · ${(detection.gridVerticalPositions ?? []).map((value, index) => `V${index + 1}=${value.toFixed(4)}`).join(" · ") || "–"}`);
    lines.push(formatGeometry("Grid-Homographie", detection.gridGeometry));
    lines.push(`Grid-Homographie-Ecken · ${formatCorners(detection.gridHomographyCorners)}`);
    lines.push(`Grid-Achse H · ${JSON.stringify(detection.gridHorizontalAxis)}`);
    lines.push(`Grid-Achse V · ${JSON.stringify(detection.gridVerticalAxis)}`);
    lines.push(`Grid-Reconstruction · ${detection.gridReconstructionUsed ? "verwendet" : "nicht verwendet"} · Score ${detection.gridReconstructionScore.toFixed(3)} · ${detection.gridReconstructionReason ?? "–"}`);
    lines.push(formatGeometry("Grid-Reconstruction-Geometrie", detection.gridReconstructionGeometry));
    lines.push(`Grid-Reconstruction-Ecken · ${formatCorners(detection.gridReconstructionCandidateCorners)}`);
    const orb = detection.orbDiagnostics;
    lines.push(`ORB · Score ${detection.orbScore.toFixed(3)} · Matches ${detection.orbMatches} · Inlier ${detection.orbInliers} (${(detection.orbInlierRatio * 100).toFixed(1)} %) · Phase ${orb.phase} · Modus ${orb.decisionMode}`);
    lines.push(`ORB intern · Raw ${orb.rawMatches} · Ratio ${orb.ratioMatches} · Geo ${orb.geometricMatches} · Balanced ${orb.balancedMatches} · Confidence ${orb.confidenceScore.toFixed(2)} · Base ${orb.baseConfidenceScore.toFixed(2)} · Reprojection ${orb.reprojectionError === null ? "–" : `${orb.reprojectionError.toFixed(2)} px`}`);
    lines.push(`ORB Gates · MinArea ${orb.internalMinAreaRatio === null ? "–" : `${(orb.internalMinAreaRatio * 100).toFixed(1)} %`} · interner GeoScore ${orb.internalGeometryScore === null ? "–" : orb.internalGeometryScore.toFixed(3)} · Accepted ${orb.accepted ? "ja" : "nein"} · ${orb.rejectionReason ?? orb.error ?? "–"}`);
    lines.push(formatGeometry("ORB-Kandidatengeometrie", orb.candidateGeometry));
    lines.push(`ORB-Kandidatenecken · ${formatCorners(orb.candidateCorners)}`);
    lines.push(`ORB Spatial · ${JSON.stringify(orb.spatial)}`);
    lines.push(`Final-Geometry · ${detection.finalGeometry.accepted ? "akzeptiert" : "abgelehnt"} · H ${detection.finalGeometry.horizontalSupported}/4 (min 3) · V ${detection.finalGeometry.verticalSupported}/3 (min 2) · Mittel ${(detection.finalGeometry.averageSupport * 100).toFixed(1)} % · ${detection.finalGeometry.reason ?? "–"}`);
    if (detection.cornerRefinement) {
      lines.push(`RAW Corner Refinement · ${detection.cornerRefinement.applied ? "angewendet" : "kein sicherer Shift"} · Ø ${detection.cornerRefinement.averageShiftPx.toFixed(2)} px · max ${detection.cornerRefinement.maxShiftPx.toFixed(2)} px · Edge ${detection.cornerRefinement.edgeScores.map((value) => value.toFixed(1)).join("/")}`);
    }
    if (detection.topKRerank) {
      lines.push(`Top-K Reranking · ${detection.topKRerank.attempted ? "geprüft" : "nicht erforderlich"} · Gewinner #${detection.topKRerank.selectedCandidateIndex ?? "–"}${detection.topKRerank.changedWinner ? " · geändert" : ""}`);
      detection.topKRerank.probes.forEach((probe) => lines.push(`Top-K #${probe.candidateIndex} · Geo ${probe.geometryScore.toFixed(3)} · OCR ${probe.materialNumber || "–"} · Conf ${probe.confidence === null ? "–" : probe.confidence.toFixed(1)} · E2E ${probe.rerankScore.toFixed(3)}`));
    }
    lines.push(`Final-Support H · ${(detection.finalGeometry.horizontalSupports ?? []).map((value, index) => `H${index + 1} ${(value * 100).toFixed(1)}%`).join(" · ") || "–"}`);
    lines.push(`Final-Support V · ${(detection.finalGeometry.verticalSupports ?? []).map((value, index) => `V${index + 1} ${(value * 100).toFixed(1)}%`).join(" · ") || "–"} · Min ${((detection.finalGeometry.minimumLineRatio ?? 0.16) * 100).toFixed(1)}%`);
    lines.push(`Target-Prior · ${detection.targetGuidance.available ? "verfügbar" : "nicht verfügbar"} · Ecken ${formatCorners(detection.targetGuidance.targetCorners)} · Kandidatendistanz ${detection.targetGuidance.selectedCornerDistanceRatio === null ? "–" : `${(detection.targetGuidance.selectedCornerDistanceRatio * 100).toFixed(1)} %`} · Rescue ${detection.targetGuidance.rescueUsed ? detection.targetGuidance.rescueMethod ?? "ja" : "nein"}`);
    lines.push(`Quick-Warp 900×216 · ${detection.quickWarp.checked ? detection.quickWarp.accepted ? "akzeptiert" : "abgelehnt" : "nicht geprüft"} · H ${detection.quickWarp.horizontalSupported}/4 (min 3) · V ${detection.quickWarp.verticalSupported}/3 (min 2) · Mittel ${(detection.quickWarp.averageSupport * 100).toFixed(1)} % · ${detection.quickWarp.reason ?? "–"}`);
    lines.push(`Quick-Support H · ${(detection.quickWarp.horizontalSupports ?? []).map((value, index) => `H${index + 1} ${(value * 100).toFixed(1)}%`).join(" · ") || "–"}`);
    lines.push(`Quick-Support V · ${(detection.quickWarp.verticalSupports ?? []).map((value, index) => `V${index + 1} ${(value * 100).toFixed(1)}%`).join(" · ") || "–"} · Min ${((detection.quickWarp.minimumLineRatio ?? 0.16) * 100).toFixed(1)}%`);
    for (const hypothesis of detection.gridHypotheses.slice(0, 8)) {
      lines.push(`Grid-Hypothese #${hypothesis.index}${hypothesis.selected ? " [gewählt]" : ""} · Gesamt ${hypothesis.combinedScore.toFixed(3)} · Vor ${hypothesis.preliminaryScore.toFixed(3)} · H ${hypothesis.horizontalAxis.matches.length} (${hypothesis.horizontalAxis.residual?.toFixed(4) ?? "–"}) · V ${hypothesis.verticalAxis.matches.length} (${hypothesis.verticalAxis.residual?.toFixed(4) ?? "–"}) · Aspect ${hypothesis.geometry.aspectRatio?.toFixed(3) ?? "–"} · Geo ${hypothesis.geometry.geometryScore?.toFixed(3) ?? "–"} · Target ${hypothesis.targetDistanceRatio === null ? "–" : `${(hypothesis.targetDistanceRatio * 100).toFixed(1)} %`} · Quick ${hypothesis.quickWarp.checked ? `${hypothesis.quickWarp.accepted ? "OK" : "Nein"} ${(hypothesis.quickWarp.averageSupport * 100).toFixed(1)} %` : "–"}`);
    }
    lines.push(`Finale Detection-Ecken · ${formatCorners(detection.corners)}`);
    lines.push(`RAW-Ecken · ${formatCorners(detection.rawCorners)}`);
    for (const candidate of detection.candidates) {
      lines.push(`Kontur-Kandidat #${candidate.index} · Score ${candidate.score?.toFixed(3) ?? "–"} · Fläche ${candidate.areaRatio === undefined ? "–" : `${(candidate.areaRatio * 100).toFixed(2)} %`} · Aspect ${candidate.aspectRatio?.toFixed(3) ?? "–"} · Rechteck ${candidate.rectangularity?.toFixed(3) ?? "–"} · Outer ${candidate.eligibleAsOuterLabel ? "ja" : "nein"} · ${formatCorners(candidate.corners)}`);
    }
    lines.push(`Ausschlussstatistik · ${JSON.stringify(detection.exclusionStats)}`);
    detection.rejectionChain.forEach((entry, index) => lines.push(`Entscheidung ${index + 1} · ${entry}`));
  }
  lines.push(`Perspektivkorrektur · ${details.rectification.used ? `${details.rectification.width ?? "–"}×${details.rectification.height ?? "–"} px` : details.rectification.error ?? "nicht verwendet"}`);
  lines.push(`Rectification-Input · Modus ${details.rectification.sourceMode ?? "–"} · ${details.rectification.inputWidth ?? "–"}×${details.rectification.inputHeight ?? "–"} · MinArea ${details.rectification.minimumAreaRatio === null || details.rectification.minimumAreaRatio === undefined ? "–" : `${(details.rectification.minimumAreaRatio * 100).toFixed(2)} %`} · Fläche ${details.rectification.areaRatio === null || details.rectification.areaRatio === undefined ? "–" : `${(details.rectification.areaRatio * 100).toFixed(2)} %`} · Aspect ${details.rectification.aspectRatio?.toFixed(3) ?? "–"}`);
  lines.push(`Rectification-Ecken · ${formatCorners(details.rectification.inputCorners)}`);
  for (const line of details.debugLines) lines.push(line);
  if (details.ocr) {
    lines.push(`OCR · ${details.ocr.engine} · ${details.ocr.languages} · Status ${details.ocr.status} · ${details.ocr.durationMs ?? "–"} ms`);
    if (details.ocr.gridAnchoring) {
      const grid = details.ocr.gridAnchoring;
      lines.push(`OCR Grid-Anker · ${grid.applied ? "aktiv" : "Fallback"} · X-Matches ${grid.horizontalMatches} · Y-Matches ${grid.verticalMatches} · Residual ${grid.meanResidual === null ? "–" : `${(grid.meanResidual * 100).toFixed(2)} %`} · ${grid.reason}`);
    }
    for (const field of details.ocr.fields) {
      lines.push(`OCR ${field.label} · "${field.text || "–"}" · Confidence ${field.confidence !== null ? field.confidence.toFixed(1) : "–"} · ROI ${field.rectangle.left}/${field.rectangle.top}/${field.rectangle.width}/${field.rectangle.height} · ${field.roiSource ?? "configured"} · Pass ${field.selectedPass ?? "base"}/${field.passCount ?? 1}`);
      for (const pass of field.passes ?? []) {
        lines.push(`OCR ${field.label} Pass ${pass.pass} · "${pass.text || "–"}" · Confidence ${pass.confidence === null ? "–" : pass.confidence.toFixed(1)} · Score ${pass.score.toFixed(1)} · ${pass.durationMs} ms`);
      }
    }
    if (details.ocr.masterResolution) {
      const resolution = details.ocr.masterResolution;
      lines.push(`OCR Master-Auflösung · ${resolution.attempted ? "geprüft" : "nicht erforderlich"} · Material ${resolution.materialNumber || "–"} · gefunden ${resolution.materialFound ? "ja" : "nein"} · korrigiert ${resolution.resolvedFieldCount} · Quelle ${resolution.masterDataSource ?? "–"}${resolution.error ? ` · ${resolution.error}` : ""}`);
      for (const field of resolution.fields) {
        lines.push(`OCR Master ${field.field} · ${field.source} · "${field.ocrValue || "–"}" → "${field.resolvedValue || "–"}" · Similarity ${field.similarity === null ? "–" : `${(field.similarity * 100).toFixed(1)} %`} · ${field.reason}`);
      }
    }
    if (details.ocr.error) lines.push(`OCR Fehler · ${details.ocr.error}`);
  }
  return Array.from(new Set(lines));
}

function drawPolygon(
  context: CanvasRenderingContext2D,
  corners: { x: number; y: number }[],
  strokeStyle: string,
  lineWidth: number,
  dash: number[] = []
): void {
  if (corners.length !== 4) return;
  context.save();
  context.strokeStyle = strokeStyle;
  context.lineWidth = lineWidth;
  context.setLineDash(dash);
  context.beginPath();
  context.moveTo(corners[0].x, corners[0].y);
  corners.slice(1).forEach((corner) => context.lineTo(corner.x, corner.y));
  context.closePath();
  context.stroke();
  context.restore();
}

function mapDetectionCornerToSource(
  corner: { x: number; y: number },
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number
): { x: number; y: number } {
  return {
    x: (corner.x / Math.max(1, sourceWidth)) * targetWidth,
    y: (corner.y / Math.max(1, sourceHeight)) * targetHeight,
  };
}

const DEBUG_REFERENCE_HORIZONTAL = [0.04228, 0.33640, 0.59007, 0.95037] as const;
const DEBUG_REFERENCE_VERTICAL = [0.01988, 0.11087, 0.45097, 0.55080, 0.88207, 0.98189] as const;
const DEBUG_REFERENCE_OUTER_LEFT = 0.01988;
const DEBUG_REFERENCE_OUTER_RIGHT = 0.98189;
const DEBUG_REFERENCE_OUTER_TOP = 0.04228;
const DEBUG_REFERENCE_OUTER_BOTTOM = 0.95037;

function referenceToLabelX(value: number): number {
  return Math.max(0, Math.min(1, (value - DEBUG_REFERENCE_OUTER_LEFT) / (DEBUG_REFERENCE_OUTER_RIGHT - DEBUG_REFERENCE_OUTER_LEFT)));
}

function referenceToLabelY(value: number): number {
  return Math.max(0, Math.min(1, (value - DEBUG_REFERENCE_OUTER_TOP) / (DEBUG_REFERENCE_OUTER_BOTTOM - DEBUG_REFERENCE_OUTER_TOP)));
}

function drawBadge(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  color: string,
  font = "700 18px sans-serif"
): void {
  context.save();
  context.font = font;
  const metrics = context.measureText(text);
  const width = metrics.width + 12;
  context.fillStyle = "rgba(0,0,0,0.76)";
  context.fillRect(x, y - 20, width, 25);
  context.fillStyle = color;
  context.fillText(text, x + 6, y);
  context.restore();
}

function drawPointMarker(
  context: CanvasRenderingContext2D,
  point: { x: number; y: number },
  label: string,
  color: string
): void {
  context.save();
  context.fillStyle = color;
  context.beginPath();
  context.arc(point.x, point.y, 7, 0, Math.PI * 2);
  context.fill();
  context.strokeStyle = "rgba(0,0,0,0.82)";
  context.lineWidth = 3;
  context.stroke();
  drawBadge(context, label, point.x + 8, Math.max(22, point.y - 6), color, "700 16px sans-serif");
  context.restore();
}

function projectIntoQuad(corners: { x: number; y: number }[], u: number, v: number): { x: number; y: number } {
  if (corners.length !== 4) return { x: 0, y: 0 };
  const [tl, tr, br, bl] = corners;
  const top = { x: tl.x + (tr.x - tl.x) * u, y: tl.y + (tr.y - tl.y) * u };
  const bottom = { x: bl.x + (br.x - bl.x) * u, y: bl.y + (br.y - bl.y) * u };
  return { x: top.x + (bottom.x - top.x) * v, y: top.y + (bottom.y - top.y) * v };
}

function drawReferenceGridOnQuad(
  context: CanvasRenderingContext2D,
  corners: { x: number; y: number }[],
  quickWarp: ScanQuickWarpDiagnostics
): void {
  if (corners.length !== 4) return;
  const minimum = typeof quickWarp?.minimumLineRatio === "number" ? quickWarp.minimumLineRatio : 0.16;
  const hSupports: number[] = quickWarp?.horizontalSupports ?? [];
  const vSupports: number[] = quickWarp?.verticalSupports ?? [];

  DEBUG_REFERENCE_HORIZONTAL.forEach((reference, index) => {
    const v = referenceToLabelY(reference);
    const a = projectIntoQuad(corners, 0, v);
    const b = projectIntoQuad(corners, 1, v);
    const support = hSupports[index];
    const accepted = typeof support === "number" ? support >= minimum : null;
    const color = accepted === true ? "#52e28b" : accepted === false ? "#ff5b6e" : "#ffe066";
    context.save();
    context.strokeStyle = color;
    context.lineWidth = index === 0 || index === DEBUG_REFERENCE_HORIZONTAL.length - 1 ? 4 : 2;
    context.setLineDash(accepted === false ? [8, 6] : []);
    context.beginPath();
    context.moveTo(a.x, a.y);
    context.lineTo(b.x, b.y);
    context.stroke();
    context.restore();
    if (typeof support === "number") drawBadge(context, `QH${index + 1} ${(support * 100).toFixed(0)}%`, a.x + 4, Math.max(20, a.y - 4), color, "700 14px sans-serif");
  });

  const validationVerticalReferences = [DEBUG_REFERENCE_OUTER_LEFT, 0.45097, DEBUG_REFERENCE_OUTER_RIGHT];
  DEBUG_REFERENCE_VERTICAL.forEach((reference) => {
    const u = referenceToLabelX(reference);
    const a = projectIntoQuad(corners, u, 0);
    const b = projectIntoQuad(corners, u, 1);
    const validationIndex = validationVerticalReferences.findIndex((value) => Math.abs(value - reference) < 1e-6);
    const support = validationIndex >= 0 ? vSupports[validationIndex] : undefined;
    const accepted = typeof support === "number" ? support >= minimum : null;
    const isValidation = validationIndex >= 0;
    const color = accepted === true ? "#52e28b" : accepted === false ? "#ff5b6e" : isValidation ? "#ffe066" : "rgba(255,255,255,0.55)";
    context.save();
    context.strokeStyle = color;
    context.lineWidth = isValidation ? 3 : 1.5;
    context.setLineDash(accepted === false ? [7, 5] : isValidation ? [] : [4, 5]);
    context.beginPath();
    context.moveTo(a.x, a.y);
    context.lineTo(b.x, b.y);
    context.stroke();
    context.restore();
    if (typeof support === "number") drawBadge(context, `QV${validationIndex + 1} ${(support * 100).toFixed(0)}%`, a.x + 4, Math.max(20, a.y - 4), color, "700 14px sans-serif");
  });
}

function drawDetectionOverlay(
  context: CanvasRenderingContext2D,
  details: ScanCaptureDetails,
  width: number,
  height: number
): void {
  const detection = details.detection;
  if (!detection) return;
  const toCanvas = (corner: { x: number; y: number }) => mapDetectionCornerToSource(
    corner,
    detection.imageWidth,
    detection.imageHeight,
    width,
    height
  );
  const normalizedToCanvas = (corner: { x: number; y: number }) => ({ x: corner.x * width, y: corner.y * height });

  // 1) Alle im Grid-Preprocessing gefundenen Linien.
  context.save();
  context.lineWidth = 1.2;
  context.setLineDash([5, 7]);
  for (const [index, position] of (detection.gridHorizontalPositions ?? []).entries()) {
    const y = position * height;
    context.strokeStyle = "rgba(90,205,255,0.42)";
    context.beginPath(); context.moveTo(0, y); context.lineTo(width, y); context.stroke();
    drawBadge(context, `H${index + 1}`, 8, Math.max(20, y - 3), "#5acdf8", "700 13px sans-serif");
  }
  for (const [index, position] of (detection.gridVerticalPositions ?? []).entries()) {
    const x = position * width;
    context.strokeStyle = "rgba(201,140,255,0.42)";
    context.beginPath(); context.moveTo(x, 0); context.lineTo(x, height); context.stroke();
    drawBadge(context, `V${index + 1}`, Math.min(width - 48, x + 4), 22, "#c98cff", "700 13px sans-serif");
  }
  context.restore();

  // 2) Tatsächlich gematchte Achsenlinien inklusive Referenz-Zuordnung.
  for (const match of detection.gridHorizontalAxis.matches) {
    const y = match.observed * height;
    context.save(); context.strokeStyle = "#35bdf2"; context.lineWidth = 3; context.beginPath(); context.moveTo(0, y); context.lineTo(width, y); context.stroke(); context.restore();
    drawBadge(context, `H ref ${match.reference.toFixed(3)}`, 80, Math.max(20, y - 4), "#35bdf2", "700 14px sans-serif");
  }
  for (const match of detection.gridVerticalAxis.matches) {
    const x = match.observed * width;
    context.save(); context.strokeStyle = "#c98cff"; context.lineWidth = 3; context.beginPath(); context.moveTo(x, 0); context.lineTo(x, height); context.stroke(); context.restore();
    drawBadge(context, `V ref ${match.reference.toFixed(3)}`, Math.min(width - 150, x + 4), 46, "#c98cff", "700 14px sans-serif");
  }

  // 3) Viewfinder-Target als weiche Zielvorgabe.
  if (detection.targetGuidance.targetCorners?.length === 4) {
    const target = detection.targetGuidance.targetCorners.map(normalizedToCanvas);
    drawPolygon(context, target, "#4cff9a", 4, [14, 8]);
    drawBadge(context, "TARGET / Viewfinder-Prior", target[0].x + 8, Math.max(24, target[0].y - 8), "#4cff9a");
    target.forEach((point, index) => drawPointMarker(context, point, `T${index + 1}`, "#4cff9a"));
  }

  // 4) Grid-Homographie und Reconstruction, auch wenn sie verworfen wurden.
  if (detection.gridHomographyCorners?.length === 4) {
    const mapped = detection.gridHomographyCorners.map(normalizedToCanvas);
    drawPolygon(context, mapped, "#ff8a4c", 4, [10, 6]);
    drawBadge(context, `GRID-H ${detection.gridGeometry.accepted ? "OK" : "REJECT"}`, mapped[0].x + 8, Math.max(24, mapped[0].y - 8), "#ff8a4c");
  }
  if (detection.gridReconstructionCandidateCorners?.length === 4) {
    const mapped = detection.gridReconstructionCandidateCorners.map(normalizedToCanvas);
    drawPolygon(context, mapped, "#ff5fd1", 4, [12, 6]);
    drawBadge(context, `RECON ${detection.gridReconstructionGeometry.accepted ? "GEO OK" : "REJECT"}`, mapped[0].x + 8, mapped[0].y + 24, "#ff5fd1");
  }

  // 5) Mehrhypothesen: Top-5 transparent, Gewinner deutlich.
  const shownHypotheses = detection.gridHypotheses.slice(0, 5);
  for (const hypothesis of [...shownHypotheses].reverse()) {
    const mapped = hypothesis.reconstructionCorners.map(toCanvas);
    const selected = hypothesis.selected;
    const color = selected ? "#ffd400" : "rgba(255,255,255,0.48)";
    drawPolygon(context, mapped, color, selected ? 6 : 2, selected ? [] : [7, 7]);
    if (mapped[0]) {
      const quick = hypothesis.quickWarp.checked
        ? `${hypothesis.quickWarp.accepted ? "Q✓" : "Q✕"} H${hypothesis.quickWarp.horizontalSupported} V${hypothesis.quickWarp.verticalSupported}`
        : "Q–";
      drawBadge(
        context,
        `G#${hypothesis.index}${selected ? " ★" : ""} A${hypothesis.geometry.aspectRatio?.toFixed(2) ?? "–"} ${quick}`,
        mapped[0].x + 6,
        Math.max(24, mapped[0].y - 6),
        selected ? "#ffd400" : "#ffffff",
        selected ? "700 17px sans-serif" : "700 14px sans-serif"
      );
    }
    if (selected) drawReferenceGridOnQuad(context, mapped, hypothesis.quickWarp);
  }

  // 6) ORB-Projektion, auch wenn intern verworfen.
  if (detection.orbDiagnostics.candidateCorners?.length === 4) {
    const mapped = detection.orbDiagnostics.candidateCorners.map(toCanvas);
    drawPolygon(context, mapped, "#ff4d67", 3, [6, 5]);
    drawBadge(context, `ORB ${detection.orbDiagnostics.accepted ? "OK" : "REJECT"}`, mapped[0].x + 8, mapped[0].y + 46, "#ff4d67");
  }

  // 7) Konturkandidaten.
  for (const candidate of detection.candidates.slice(0, 8)) {
    const mapped = candidate.corners.map(toCanvas);
    drawPolygon(context, mapped, "#35bdf2", 2.5, [10, 7]);
    const first = mapped[0];
    if (first) drawBadge(context, `C#${candidate.index} A${candidate.aspectRatio?.toFixed(2) ?? "–"}`, first.x + 6, Math.max(24, first.y - 6), "#35bdf2", "700 14px sans-serif");
  }

  // 8) Final gewählte Detection-Ecken ganz obenauf.
  if (detection.corners?.length === 4) {
    const mapped = detection.corners.map(toCanvas);
    drawPolygon(context, mapped, "#00ff80", 8);
    mapped.forEach((corner, index) => drawPointMarker(context, corner, `P${index + 1}`, "#00ff80"));
    drawBadge(context, "FINAL DETECTION", mapped[0].x + 8, Math.max(24, mapped[0].y - 34), "#00ff80");
  }
}

function selectedGridHypothesis(details: ScanCaptureDetails) {
  return details.detection?.gridHypotheses.find((entry) => entry.selected) ?? details.detection?.gridHypotheses[0] ?? null;
}

function getQuickWarpPanelHeight(details: ScanCaptureDetails, canvasWidth: number): number {
  const hypothesis = selectedGridHypothesis(details);
  if (!details.detection || !hypothesis || hypothesis.reconstructionCorners.length !== 4) return 0;
  const previewWidth = Math.max(600, Math.min(1200, canvasWidth - 48));
  return 66 + Math.round(previewWidth * 216 / 900) + 42;
}

function drawQuickWarpDiagnosticPanel(
  context: CanvasRenderingContext2D,
  sourceImage: HTMLImageElement,
  details: ScanCaptureDetails,
  top: number,
  canvasWidth: number
): number {
  const detection = details.detection;
  const hypothesis = selectedGridHypothesis(details);
  if (!detection || !hypothesis || hypothesis.reconstructionCorners.length !== 4) return 0;

  const headerHeight = 66;
  const maxPreviewWidth = Math.min(1200, canvasWidth - 48);
  const previewWidth = Math.max(600, maxPreviewWidth);
  const previewHeight = Math.round(previewWidth * 216 / 900);
  const panelHeight = headerHeight + previewHeight + 42;
  context.fillStyle = "#101010";
  context.fillRect(0, top, canvasWidth, panelHeight);
  context.fillStyle = "#fff";
  context.font = "700 26px sans-serif";
  context.fillText(`Quick-Warp / Rasterdiagnose · Grid-Hypothese #${hypothesis.index}${hypothesis.selected ? " [gewählt]" : ""}`, 24, top + 40);

  const corners = hypothesis.reconstructionCorners;
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  const left = Math.max(0, Math.min(...xs));
  const topPx = Math.max(0, Math.min(...ys));
  const right = Math.min(detection.imageWidth, Math.max(...xs));
  const bottom = Math.min(detection.imageHeight, Math.max(...ys));
  const sx = (left / detection.imageWidth) * sourceImage.naturalWidth;
  const sy = (topPx / detection.imageHeight) * sourceImage.naturalHeight;
  const sw = Math.max(1, ((right - left) / detection.imageWidth) * sourceImage.naturalWidth);
  const sh = Math.max(1, ((bottom - topPx) / detection.imageHeight) * sourceImage.naturalHeight);
  const dx = 24;
  const dy = top + headerHeight;
  context.drawImage(sourceImage, sx, sy, sw, sh, dx, dy, previewWidth, previewHeight);

  const quick = hypothesis.quickWarp;
  const minimum = quick.minimumLineRatio ?? details.detection?.visionSnapshot?.detectionParameters.FINAL_SUPPORT_MIN_LINE_RATIO ?? 0.16;
  const hSupports = quick.horizontalSupports ?? [];
  const vSupports = quick.verticalSupports ?? [];

  DEBUG_REFERENCE_HORIZONTAL.forEach((reference, index) => {
    const y = dy + referenceToLabelY(reference) * previewHeight;
    const support = hSupports[index];
    const ok = typeof support === "number" ? support >= minimum : null;
    const color = ok === true ? "#52e28b" : ok === false ? "#ff5b6e" : "#ffe066";
    context.save(); context.strokeStyle = color; context.lineWidth = 3; context.setLineDash(ok === false ? [8, 6] : []); context.beginPath(); context.moveTo(dx, y); context.lineTo(dx + previewWidth, y); context.stroke(); context.restore();
    drawBadge(context, `H${index + 1} ${typeof support === "number" ? `${(support * 100).toFixed(1)}%` : "–"} ${ok === true ? "✓" : ok === false ? "✕" : ""}`, dx + 8, Math.max(dy + 20, y - 4), color, "700 15px sans-serif");
  });

  const validationVertical = [DEBUG_REFERENCE_OUTER_LEFT, 0.45097, DEBUG_REFERENCE_OUTER_RIGHT];
  validationVertical.forEach((reference, index) => {
    const x = dx + referenceToLabelX(reference) * previewWidth;
    const support = vSupports[index];
    const ok = typeof support === "number" ? support >= minimum : null;
    const color = ok === true ? "#52e28b" : ok === false ? "#ff5b6e" : "#ffe066";
    context.save(); context.strokeStyle = color; context.lineWidth = 4; context.setLineDash(ok === false ? [8, 6] : []); context.beginPath(); context.moveTo(x, dy); context.lineTo(x, dy + previewHeight); context.stroke(); context.restore();
    drawBadge(context, `V${index + 1} ${typeof support === "number" ? `${(support * 100).toFixed(1)}%` : "–"} ${ok === true ? "✓" : ok === false ? "✕" : ""}`, Math.min(dx + previewWidth - 100, x + 5), dy + 24 + index * 24, color, "700 15px sans-serif");
  });

  context.font = "18px sans-serif";
  context.fillStyle = "#fff";
  const summary = `Quick-Warp: ${quick.checked ? quick.accepted ? "akzeptiert" : "abgelehnt" : "nicht geprüft"} · H ${quick.horizontalSupported}/4 (min 3) · V ${quick.verticalSupported}/3 (min 2) · Mittel ${(quick.averageSupport * 100).toFixed(1)} % · Mindest-Support ${(minimum * 100).toFixed(1)} % · ${quick.reason ?? "–"}`;
  context.fillText(summary, 24, dy + previewHeight + 28);
  return panelHeight;
}

function drawOcrOverlay(
  context: CanvasRenderingContext2D,
  details: ScanCaptureDetails,
  offsetY: number,
  scale: number
): void {
  if (!details.ocr) return;
  const colors = ["#35bdf2", "#ffb74d", "#72d991", "#ce93d8", "#ff8a80"];
  details.ocr.fields.forEach((field, index) => {
    const rectangle = field.rectangle;
    const x = rectangle.left * scale;
    const y = offsetY + rectangle.top * scale;
    const width = rectangle.width * scale;
    const height = rectangle.height * scale;
    const color = colors[index % colors.length];
    context.save();
    context.strokeStyle = color;
    context.lineWidth = 4;
    context.strokeRect(x, y, width, height);
    context.font = "700 20px sans-serif";
    const passInfo = (field.passCount ?? 1) > 1 ? ` · ${field.selectedPass ?? "?"}/${field.passCount}` : "";
    const roiInfo = field.roiSource === "grid-anchored" ? " · Grid" : "";
    const text = `${field.label}${field.text ? `: ${field.text}` : ""}${field.confidence !== null ? ` (${field.confidence.toFixed(0)}%)` : ""}${roiInfo}${passInfo}`;
    const metrics = context.measureText(text);
    const labelY = Math.max(offsetY + 22, y - 5);
    context.fillStyle = "rgba(0,0,0,0.76)";
    context.fillRect(x, labelY - 21, metrics.width + 12, 26);
    context.fillStyle = color;
    context.fillText(text, x + 6, labelY);
    context.restore();
  });
}


export async function stampScanDebugImageIdentity(
  blob: Blob,
  identity: { withdrawalNumber: number | null; serverId: number | null; clientUuid: string | null }
): Promise<Blob> {
  const file = new File([blob], "debug.jpg", { type: blob.type || "image/jpeg" });
  const image = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d");
  if (!context) return blob;
  context.drawImage(image, 0, 0);
  const text = `#ID ${identity.withdrawalNumber ?? "–"} · SID ${identity.serverId ?? "–"}`;
  context.font = "700 22px sans-serif";
  const width = Math.min(canvas.width - 24, Math.max(250, context.measureText(text).width + 28));
  context.fillStyle = "rgba(0,0,0,0.88)";
  context.fillRect(canvas.width - width - 12, 12, width, 38);
  context.fillStyle = "#fff";
  context.fillText(text, canvas.width - width, 39);
  return new Promise((resolve) => canvas.toBlob((next) => resolve(next ?? blob), blob.type.includes("png") ? "image/png" : "image/jpeg", 0.94));
}

export async function buildScanDebugImage(
  sourceFile: File,
  analysisFile: File | null,
  details: ScanCaptureDetails
): Promise<File> {
  const sourceImage = await loadImage(sourceFile);
  const analysisImage = analysisFile ? await loadImage(analysisFile) : null;
  const maxWidth = 1800;
  const sourceScale = Math.min(1, maxWidth / sourceImage.naturalWidth);
  const sourceWidth = Math.max(1, Math.round(sourceImage.naturalWidth * sourceScale));
  const sourceHeight = Math.max(1, Math.round(sourceImage.naturalHeight * sourceScale));

  const analysisScale = analysisImage ? Math.min(1, maxWidth / analysisImage.naturalWidth) : 1;
  const analysisWidth = analysisImage ? Math.max(1, Math.round(analysisImage.naturalWidth * analysisScale)) : 0;
  const analysisHeight = analysisImage ? Math.max(1, Math.round(analysisImage.naturalHeight * analysisScale)) : 0;
  const sectionGap = analysisImage ? 68 : 0;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Debug-Bild konnte nicht erzeugt werden.");

  context.font = "24px sans-serif";
  const lineWidth = Math.max(sourceWidth, analysisWidth) - 48;
  const wrappedLines = buildLines(details).flatMap((line) => wrapLine(context, line, lineWidth));
  const diagnosticHeight = Math.max(420, 92 + wrappedLines.length * 34 + 28);
  const contentWidth = Math.max(sourceWidth, analysisWidth, 900);
  const quickWarpSectionHeight = getQuickWarpPanelHeight(details, contentWidth);
  const analysisSectionHeight = analysisImage ? sectionGap + analysisHeight : 0;

  canvas.width = contentWidth;
  canvas.height = sourceHeight + quickWarpSectionHeight + analysisSectionHeight + diagnosticHeight;
  context.fillStyle = "#0f0f10";
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.drawImage(sourceImage, 0, 0, sourceWidth, sourceHeight);
  drawDetectionOverlay(context, details, sourceWidth, sourceHeight);
  context.fillStyle = "rgba(0,0,0,0.72)";
  context.fillRect(12, 12, 240, 36);
  context.fillStyle = "#fff";
  context.font = "700 22px sans-serif";
  context.fillText("OpenCV / Geometrie & Kandidaten", 22, 38);
  const identityText = `#ID ${details.identity?.withdrawalNumber ?? "–"} · SID ${details.identity?.serverId ?? "–"}`;
  context.font = "700 22px sans-serif";
  const identityWidth = Math.min(canvas.width - 280, Math.max(250, context.measureText(identityText).width + 28));
  context.fillStyle = "rgba(0,0,0,0.82)";
  context.fillRect(canvas.width - identityWidth - 12, 12, identityWidth, 36);
  context.fillStyle = "#fff";
  context.fillText(identityText, canvas.width - identityWidth, 38);

  let sectionTop = sourceHeight;
  if (quickWarpSectionHeight > 0) {
    sectionTop += drawQuickWarpDiagnosticPanel(context, sourceImage, details, sectionTop, canvas.width);
  }

  let diagnosticsTop = sectionTop;
  if (analysisImage) {
    const titleTop = sectionTop;
    context.fillStyle = "#101010";
    context.fillRect(0, titleTop, canvas.width, sectionGap + analysisHeight);
    context.fillStyle = "#fff";
    context.font = "700 26px sans-serif";
    context.fillText("OCR-Analysebild · feste ROIs", 24, titleTop + 42);
    const analysisTop = titleTop + sectionGap;
    context.drawImage(analysisImage, 0, analysisTop, analysisWidth, analysisHeight);
    drawOcrOverlay(context, details, analysisTop, analysisScale);
    diagnosticsTop = analysisTop + analysisHeight;
  }

  context.fillStyle = "#101010";
  context.fillRect(0, diagnosticsTop, canvas.width, diagnosticHeight);
  context.fillStyle = "#ffffff";
  context.font = "700 30px sans-serif";
  const idText = `#ID ${details.identity?.withdrawalNumber ?? "–"} · SID ${details.identity?.serverId ?? "–"}`;
  context.fillText(`Etikett-Scan · Debug · ${idText}`, 24, diagnosticsTop + 44);
  context.font = "24px sans-serif";
  wrappedLines.forEach((line, index) => context.fillText(line, 24, diagnosticsTop + 88 + index * 34));

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (nextBlob) => nextBlob ? resolve(nextBlob) : reject(new Error("Debug-Bild konnte nicht kodiert werden.")),
      "image/jpeg",
      0.92
    );
  });

  return new File([blob], `Etikett_Debug_${Date.now()}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}
